import { create } from 'zustand'
import { Cart, CartItem, AddToCartRequest } from '@/types/cart'
import { cartService } from '@/services/cartService'
import { Logger } from '@/lib/logger'
import { createClient } from '@/lib/utils/supabase/client'

const supabase = createClient()

// Cache configuration
const CART_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
let lastCartFetch = 0;

interface CartStore {
  cart: Cart | null
  items: CartItem[]
  isLoading: boolean
  error: string | null
  fetchCart: () => Promise<void>
  addToCart: (request: AddToCartRequest) => Promise<void>
  removeFromCart: (itemId: number) => Promise<void>
  updateCartItem: (itemId: number, newQuantity: number) => Promise<void>
  setIsLoading: (loading: boolean) => void
  clearCart: () => void
}

export const useCartStore = create<CartStore>((set, get) => ({
  cart: null,
  items: [],
  isLoading: false,
  error: null,

  fetchCart: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      // If user is not authenticated, clear the cart state
      if (!session?.user) {
        set({ cart: null, items: [], error: null, isLoading: false })
        return
      }

      // Check if we need to fetch (cache TTL)
      const now = Date.now();
      const currentItems = get().items;
      
      // Skip fetch if we have items and within cache TTL
      if (currentItems.length > 0 && now - lastCartFetch < CART_CACHE_TTL) {
        Logger.info('Using cached cart data');
        return;
      }

      set({ isLoading: true, error: null })
      const cart = await cartService.getCart()
      lastCartFetch = now;
      
      set({ 
        cart, 
        items: cart?.items || [],
        isLoading: false 
      })
    } catch (error) {
      Logger.error('Error fetching cart', { error })
      set({ error: error as string, isLoading: false })
    }
  },

  addToCart: async (request: AddToCartRequest) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.user) {
        throw new Error('Please log in to add items to cart')
      }

      set({ isLoading: true })
      await cartService.addToCart(request)
      
      const updatedCart = await cartService.getCart()
      set({ 
        cart: updatedCart, 
        items: updatedCart?.items || [],
        isLoading: false 
      })
    } catch (error) {
      Logger.error('Error adding to cart', { error })
      set({ error: error as string, isLoading: false })
      throw error
    }
  },

  removeFromCart: async (itemId: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.user) {
        throw new Error('Please log in to remove items from cart')
      }

      set({ isLoading: true, error: null })
      await cartService.removeFromCart(itemId)
      const cart = await cartService.getCart()
      set({ 
        cart, 
        items: cart?.items || [],
        isLoading: false 
      })
    } catch (error) {
      Logger.error('Error removing from cart', { error })
      set({ error: error as string, isLoading: false })
      throw error
    }
  },

  updateCartItem: async (itemId: number, newQuantity: number) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      if (!session?.user) {
        throw new Error('Please log in to update cart items')
      }

      set({ isLoading: true, error: null })
      await cartService.updateCartItem(itemId, newQuantity)
      const cart = await cartService.getCart()
      set({ 
        cart, 
        items: cart?.items || [],
        isLoading: false 
      })
    } catch (error) {
      Logger.error('Error updating cart item', { error })
      set({ error: error as string, isLoading: false })
      throw error
    }
  },

  setIsLoading: (loading: boolean) => {
    set({ isLoading: loading })
  },

  clearCart: () => {
    set({ cart: null, items: [], error: null, isLoading: false })
  }
}))